import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SidBarClient from "./SideBarClient";
import { MagasinContext } from "../../../Context/MagasinContext";
import "./NotificationClient.css";

interface NotifItem {
  id: number;
  Title: string;
  Content: string;
  Date: string;
  IsRead: number;
  Url?: string;
  NotifiableId: string;
}

export default function NotificationClient() {
  const magasinContext = useContext(MagasinContext);
  const ClientId = localStorage.getItem("ClientId");
  const id = ClientId || magasinContext.id?.id;

  const [notifications, setNotifications] = useState<NotifItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotif, setSelectedNotif] = useState<NotifItem | null>(null);
  const [popup, setPopup] = useState(false);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_PHP_APP_URL}/client/${id}/notifications`
      );
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchNotifications();
  }, [id]);

  const formatDate = (raw: string) => {
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const markAsRead = async (notifId: number) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/client/${id}/notification/${notifId}`
      );
    } catch {
      /* silent */
    }
  };

  const handleClickNotif = (notif: NotifItem) => {
    setSelectedNotif(notif);
    setPopup(true);
    if (notif.IsRead !== 1) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, IsRead: 1 } : n))
      );
      markAsRead(notif.id);
    }
  };

  const unreadCount = notifications.filter((n) => n.IsRead === 0).length;

  return (
    <div className="notif-cl-wrapper">
      <SidBarClient />

      <main className="notif-cl-main">
        {/* Header */}
        <div className="notif-cl-header">
          <div className="notif-cl-header__left">
            <div className="notif-cl-header__icon">
              <i className="bi bi-bell-fill" />
            </div>
            <div>
              <h1 className="notif-cl-header__title">Notifications</h1>
              <p className="notif-cl-header__subtitle">
                Restez informé de toutes les activités de votre compte
              </p>
            </div>
          </div>
          {unreadCount > 0 && (
            <div className="notif-cl-unread-badge">
              {unreadCount} non lue{unreadCount > 1 ? "s" : ""}
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="notif-cl-loader">
            <div className="notif-cl-spinner" />
            <span className="notif-cl-loader-text">
              Chargement des notifications...
            </span>
          </div>
        ) : notifications.length > 0 ? (
          <div className="notif-cl-list">
            {notifications.map((notif, index) => (
              <div
                key={notif.id}
                className={`notif-cl-card ${
                  notif.IsRead === 1 ? "read-cl" : "unread-cl"
                }`}
                onClick={() => handleClickNotif(notif)}
                style={{ animationDelay: `${index * 0.06}s` }}
              >
                {/* Unread dot */}
                {notif.IsRead === 0 && (
                  <div className="notif-cl-card__dot" />
                )}

                {/* Icon */}
                <div
                  className={`notif-cl-card__icon ${
                    notif.IsRead === 1 ? "read-icon-cl" : "unread-icon-cl"
                  }`}
                >
                  <i className="bi bi-bell-fill" />
                </div>

                {/* Content */}
                <div className="notif-cl-card__content">
                  <div className="notif-cl-card__row">
                    <h3 className="notif-cl-card__title">{notif.Title}</h3>
                    <span className="notif-cl-card__date">
                      {formatDate(notif.Date)}
                    </span>
                  </div>
                  <p className="notif-cl-card__msg">{notif.Content}</p>
                  <div className="notif-cl-card__footer">
                    <span
                      className={`notif-cl-card__status ${
                        notif.IsRead === 1
                          ? "read-status-cl"
                          : "unread-status-cl"
                      }`}
                    >
                      {notif.IsRead === 1 ? (
                        <>
                          <i className="bi bi-check-circle-fill" />
                          <span>Déjà lue</span>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-circle-fill" />
                          <span>Nouvelle</span>
                        </>
                      )}
                    </span>
                    <span className="notif-cl-card__arrow">
                      <i className="bi bi-arrow-right" />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="notif-cl-empty">
            <div className="notif-cl-empty__icon-wrap">
              <i className="bi bi-bell-slash" />
            </div>
            <h2 className="notif-cl-empty__title">Aucune notification</h2>
            <p className="notif-cl-empty__text">
              Nous vous préviendrons dès qu'il y aura du nouveau.
              <br />
              Revenez plus tard pour vérifier vos notifications.
            </p>
          </div>
        )}
      </main>

      {/* Modal */}
      {popup && selectedNotif && (
        <div
          className="notif-cl-modal-overlay"
          onClick={() => setPopup(false)}
        >
          <div
            className="notif-cl-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="notif-cl-modal__close"
              onClick={() => setPopup(false)}
              aria-label="Fermer"
            >
              <i className="bi bi-x-lg" />
            </button>

            <div className="notif-cl-modal__icon-wrap">
              <i className="bi bi-bell-fill" />
            </div>

            <h2 className="notif-cl-modal__title">{selectedNotif.Title}</h2>
            <p className="notif-cl-modal__msg">{selectedNotif.Content}</p>

            <div className="notif-cl-modal__meta">
              <span className="notif-cl-modal__date">
                <i className="bi bi-clock" />
                {formatDate(selectedNotif.Date)}
              </span>
            </div>

            <div className="notif-cl-modal__actions">
              {selectedNotif.Url ? (
                <Link
                  to={selectedNotif.Url}
                  className="notif-cl-modal__btn primary-cl"
                  onClick={() => setPopup(false)}
                >
                  <span>Voir les détails</span>
                  <i className="bi bi-arrow-right" />
                </Link>
              ) : (
                <button
                  className="notif-cl-modal__btn primary-cl"
                  onClick={() => setPopup(false)}
                >
                  <span>D'accord</span>
                  <i className="bi bi-check-lg" />
                </button>
              )}
              <button
                className="notif-cl-modal__btn secondary-cl"
                onClick={() => setPopup(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
